import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@bcc/components/ui/accordion";
import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Checkbox } from "@bcc/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bcc/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { format } from "date-fns";
import { AlertCircle, CalendarIcon, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setEditProductModalTestId } from "./edit-product-modal.utils";

type Product = {
  id: number;
  name: string;
  sku: string;
  category: string;
  imageUrl: string;
  description: string;
  variants: Array<{
    id: string;
    size: string;
    sku: string;
    status: "published" | "out_of_stock" | "rejected" | "draft";
    subscribers: number;
    purchasePrice: number;
    salePrice: number;
    commission: number;
    payout: number;
    margin: number;
    weeklyLimit: number;
    termType: "long_term" | "short_term";
    termEndDate?: string;
    expectedOutOfStockDate?: string;
    grindMappings: string[];
    stock: number;
  }>;
};

interface EditProductModalProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  testId?: string;
}

export function EditProductModal({
  product,
  open,
  onOpenChange,
  onSave,
  testId,
}: EditProductModalProps) {
  const [editedProduct, setEditedProduct] = useState(product);
  const [hasChanges, setHasChanges] = useState(false);
  const { t } = useTranslation();

  // Calculate margin in real-time
  const calculateMargin = (salePrice: number, purchasePrice: number) => {
    if (salePrice === 0) return 0;
    return ((salePrice - purchasePrice) / salePrice) * 100;
  };

  const updateVariant = (variantId: string, field: string, value: any) => {
    setEditedProduct((prev) => ({
      ...prev,
      variants: prev.variants.map((v) => {
        if (v.id === variantId) {
          const updated = { ...v, [field]: value };
          // Recalculate margin if price changes
          if (field === "salePrice" || field === "purchasePrice") {
            updated.margin = calculateMargin(
              field === "salePrice" ? value : v.salePrice,
              field === "purchasePrice" ? value : v.purchasePrice,
            );
          }
          return updated;
        }
        return v;
      }),
    }));
    setHasChanges(true);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      published: "bg-green-500/10 text-green-700 dark:text-green-400",
      out_of_stock: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      rejected: "bg-red-500/10 text-red-700 dark:text-red-400",
      draft: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    };
    return styles[status as keyof typeof styles] || styles.draft;
  };

  const handleSubmit = () => {
    console.log("[v0] Submitting product changes:", editedProduct);
    // API call would go here
    onSave();
  };

  // Check for validation errors
  const hasLowMargin = editedProduct.variants.some((v) => v.margin < 50);
  const hasMissingGrindMapping = editedProduct.variants.some(
    (v) => v.grindMappings.length === 0,
  );
  const hasMissingTerm = editedProduct.variants.some((v) => !v.termType);

  const canSubmit = !hasMissingGrindMapping && !hasMissingTerm && hasChanges;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        testId={testId ?? setEditProductModalTestId("content")}
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t("views.editProductModal.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Validation Alerts */}
          {hasMissingGrindMapping && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t("views.editProductModal.alerts.grindMappingRequired")}
              </AlertDescription>
            </Alert>
          )}

          {hasLowMargin && (
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-600 dark:text-amber-400">
                {t("views.editProductModal.alerts.lowMargin")}
              </AlertDescription>
            </Alert>
          )}

          {/* Product Information */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
            <h3 className="font-semibold text-lg">
              {t("views.editProductModal.productInformation")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t("views.editProductModal.productName")}
                </Label>
                <Input
                  id="name"
                  value={editedProduct.name}
                  onChange={(e) => {
                    setEditedProduct({
                      ...editedProduct,
                      name: e.target.value,
                    });
                    setHasChanges(true);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">
                  {t("views.editProductModal.category")}
                </Label>
                <Input
                  id="category"
                  value={editedProduct.category}
                  onChange={(e) => {
                    setEditedProduct({
                      ...editedProduct,
                      category: e.target.value,
                    });
                    setHasChanges(true);
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">
                {t("views.editProductModal.description")}
              </Label>
              <Textarea
                id="description"
                value={editedProduct.description}
                onChange={(e) => {
                  setEditedProduct({
                    ...editedProduct,
                    description: e.target.value,
                  });
                  setHasChanges(true);
                }}
                rows={3}
              />
            </div>
          </div>

          {/* SKU Variants Accordion */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">
              {t("views.editProductModal.productSizesVariants")}
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {editedProduct.variants.map((variant, index) => (
                <AccordionItem
                  key={variant.id}
                  value={variant.id}
                  className="border rounded-lg mb-2"
                >
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3 w-full">
                      <span className="font-medium">{variant.size}</span>
                      <Badge
                        variant="secondary"
                        className={getStatusBadge(variant.status)}
                      >
                        {variant.status.replace("_", " ")}
                      </Badge>
                      <span className="text-sm text-muted-foreground ml-auto mr-4">
                        {variant.sku}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4 pt-4">
                      {/* Pricing Panel */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`purchase-${variant.id}`}>
                            {t("views.editProductModal.wholesalePrice")}
                          </Label>
                          <Input
                            id={`purchase-${variant.id}`}
                            type="number"
                            step="0.01"
                            value={variant.purchasePrice}
                            onChange={(e) =>
                              updateVariant(
                                variant.id,
                                "purchasePrice",
                                Number.parseFloat(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`sale-${variant.id}`}>
                            {t("views.editProductModal.msrpRrp")}
                          </Label>
                          <Input
                            id={`sale-${variant.id}`}
                            type="number"
                            step="0.01"
                            value={variant.salePrice}
                            onChange={(e) =>
                              updateVariant(
                                variant.id,
                                "salePrice",
                                Number.parseFloat(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`commission-${variant.id}`}>
                            {t("views.editProductModal.commission")}
                          </Label>
                          <Input
                            id={`commission-${variant.id}`}
                            type="number"
                            step="0.1"
                            value={variant.commission}
                            onChange={(e) =>
                              updateVariant(
                                variant.id,
                                "commission",
                                Number.parseFloat(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t("views.editProductModal.margin")}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={`${variant.margin.toFixed(1)}%`}
                              disabled
                            />
                            {variant.margin >= 50 ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-amber-500" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Term & Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`term-${variant.id}`}>
                            {t("views.editProductModal.termType")}
                          </Label>
                          <Select
                            value={variant.termType}
                            onValueChange={(value: any) =>
                              updateVariant(variant.id, "termType", value)
                            }
                          >
                            <SelectTrigger id={`term-${variant.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="long_term">
                                {t("views.editProductModal.longTerm")}
                              </SelectItem>
                              <SelectItem value="short_term">
                                {t("views.editProductModal.shortTerm")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {variant.termType === "short_term" && (
                          <div className="space-y-2">
                            <Label>{t("views.editProductModal.endDate")}</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-start text-left font-normal bg-transparent"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {variant.termEndDate
                                    ? format(
                                        new Date(variant.termEndDate),
                                        "PPP",
                                      )
                                    : t("views.editProductModal.selectDate")}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={
                                    variant.termEndDate
                                      ? new Date(variant.termEndDate)
                                      : undefined
                                  }
                                  onSelect={(date) =>
                                    updateVariant(
                                      variant.id,
                                      "termEndDate",
                                      date?.toISOString().split("T")[0],
                                    )
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </div>

                      {/* Expected Out of Stock Date */}
                      {variant.termType === "short_term" && (
                        <div className="space-y-2">
                          <Label>
                            {t("views.editProductModal.expectedOutOfStockDate")}
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal bg-transparent"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {variant.expectedOutOfStockDate
                                  ? format(
                                      new Date(variant.expectedOutOfStockDate),
                                      "PPP",
                                    )
                                  : t("views.editProductModal.selectDate")}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={
                                  variant.expectedOutOfStockDate
                                    ? new Date(variant.expectedOutOfStockDate)
                                    : undefined
                                }
                                onSelect={(date) =>
                                  updateVariant(
                                    variant.id,
                                    "expectedOutOfStockDate",
                                    date?.toISOString().split("T")[0],
                                  )
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      )}

                      {/* Grind Mappings */}
                      <div className="space-y-2">
                        <Label>
                          {t("views.editProductModal.grindMappings")}
                        </Label>
                        <div className="flex flex-wrap gap-3">
                          {[
                            "Whole Bean",
                            "Espresso",
                            "Filter",
                            "French Press",
                            "Cold Brew",
                          ].map((grind) => (
                            <div
                              key={grind}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`${variant.id}-${grind}`}
                                checked={variant.grindMappings.includes(grind)}
                                onCheckedChange={(checked) => {
                                  const newMappings = checked
                                    ? [...variant.grindMappings, grind]
                                    : variant.grindMappings.filter(
                                        (g) => g !== grind,
                                      );
                                  updateVariant(
                                    variant.id,
                                    "grindMappings",
                                    newMappings,
                                  );
                                }}
                              />
                              <label
                                htmlFor={`${variant.id}-${grind}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                {grind}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Weekly Limit */}
                      <div className="space-y-2">
                        <Label htmlFor={`limit-${variant.id}`}>
                          {t("views.editProductModal.weeklyLimit")}
                        </Label>
                        <Input
                          id={`limit-${variant.id}`}
                          type="number"
                          value={variant.weeklyLimit}
                          onChange={(e) =>
                            updateVariant(
                              variant.id,
                              "weeklyLimit",
                              Number.parseInt(e.target.value),
                            )
                          }
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("views.editProductModal.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-primary"
            >
              {t("views.editProductModal.submitForApproval")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
